import { ValueSet, ValueSetExpansionContains } from "@medplum/fhirtypes";

import { ProcedureCode } from "../types/fee-table";

/**
 * Merges ValueSet expansion contains, deduplicating by system + code. Custom codes stay next to a
 * fee code with the same value, since they carry their own system and a custom label.
 * @param feeCodes Codes from the fee ValueSets
 * @param customCodes Codes from the practice's custom codes
 * @returns The merged codes, fee codes first
 */
export function mergeAndDeduplicateCodes(
  feeCodes: ValueSetExpansionContains[],
  customCodes: ValueSetExpansionContains[],
): ValueSetExpansionContains[] {
  const all = new Map<string, ValueSetExpansionContains>();

  for (const code of [...feeCodes, ...customCodes]) {
    if (!code.system || !code.code) continue;
    const key = `${code.system}|${code.code}`;
    if (!all.has(key)) {
      all.set(key, code);
    }
  }

  return Array.from(all.values());
}

/**
 * Picks the codes a stored combined ValueSet holds for one fee ValueSet. Each fee ValueSet lists
 * the codes of one CodeSystem that shares its name, such as `ValueSet/facility-fee` and
 * `CodeSystem/facility-fee`, so an entry's system names its ValueSet.
 * @param storedValueSet The combined ValueSet saved by the last rebuild, if any
 * @param feeValueSetUrl URL of the fee ValueSet whose codes to pick
 * @returns The stored codes of that ValueSet, active unless stored as inactive
 */
export function pickStoredProcedureCodesOfValueSet(
  storedValueSet: ValueSet | undefined,
  feeValueSetUrl: string,
): ProcedureCode[] {
  const codeSystemUrl = feeValueSetUrl.replace("/ValueSet/", "/CodeSystem/");
  return (storedValueSet?.expansion?.contains ?? []).reduce<ProcedureCode[]>(
    (procedureCodes, { system, code, display, inactive }) => {
      if (system === codeSystemUrl && code) {
        procedureCodes.push({
          code,
          name: display ?? "",
          information: "",
          isActive: !inactive,
          coding: { system, code, display },
        });
      }
      return procedureCodes;
    },
    [],
  );
}
